import { Alert, Button, Card, Checkbox, Form, Input, Modal, Select, Space, Tag, message } from "antd";
import type { MonitorEarlyWarnRecord, Page } from "@zhihaoscm/entity";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Pagination as ZhPagination,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { SORT, YES_OR_NO } from "@/meta/common";
import { EXCEPTION_TYPE, HANDLE_STATE } from "@/meta/logistics/monitorWarn";
import { useUserStore } from "@/stores/react/authStore";

export interface MonitorWarnQuery {
    page: number;
    size: number;
    keyword?: string;
    type?: number;
    handleState?: number;
    misinformation?: number;
    beginTime?: string;
    endTime?: string;
    sortKey?: string;
    sortOrder?: string;
}

/** 将旧 Vue 预警列表处理时间排序转换为分页接口参数。 */
export function buildMonitorWarnSortQuery(
    query: MonitorWarnQuery,
    sort: { prop?: string; order: "ascending" | "descending" | null }
): MonitorWarnQuery {
    const nextQuery = { ...query, page: 1 };
    delete nextQuery.sortKey;
    delete nextQuery.sortOrder;
    if (sort.prop === "handleTime" && sort.order) {
        nextQuery.sortKey = "handle_time";
        nextQuery.sortOrder = SORT.getIntroByCode(sort.order);
    }
    return nextQuery;
}
export interface MonitorWarnOfficer {
    user: { id: number; name: string; employeeId?: string };
    count?: number;
}
export interface MonitorWarnPageProps {
    loadRecords?: (query: MonitorWarnQuery) => Promise<Page<MonitorEarlyWarnRecord>>;
    loadCommissioners?: () => Promise<MonitorWarnOfficer[]>;
    assignWarnings?: (userId: number, ids: number[]) => Promise<unknown>;
    handleWarnings?: (form: { ids: number[]; misinformation: number; remark?: string }) => Promise<unknown>;
    permissions?: string[];
}

/** 复用 Vue 预警列表分页接口，保持查询参数和默认未处理状态。 */
export async function loadMonitorWarnRecords(query: MonitorWarnQuery) {
    const response = await shippingRequestHttp.service.get<Page<MonitorEarlyWarnRecord>>(
        "/monitor-early-warn-record/paging",
        {
            params: query
        }
    );
    return response.data;
}
/** 复用 Vue 专员选择器接口，仅在用户显式打开指派操作时调用。 */
export async function loadMonitorWarnCommissioners() {
    const response = await shippingRequestHttp.service.get<MonitorWarnOfficer[]>(
        "/monitor-early-warn-record/specials-selector"
    );
    return response.data;
}
export async function assignMonitorWarnings(userId: number, ids: number[]) {
    const response = await shippingRequestHttp.service.put(`/monitor-early-warn-record/batch-assign/${userId}`, ids);
    return response.data;
}
export async function handleMonitorWarnings(form: { ids: number[]; misinformation: number; remark?: string }) {
    const response = await shippingRequestHttp.service.put("/monitor-early-warn-record/batch-handle", form);
    return response.data;
}

const enumText = (meta: { getIntroByCode: (code?: number | string) => string }, value?: number) =>
    meta.getIntroByCode(value) || DEFAULT_VALUE;
const toDateTime = (value: string | null, end = false) =>
    value ? `${value} ${end ? "23:59:59" : "00:00:00"}` : undefined;

/** 监控预警列表，覆盖 Vue 筛选、分页、批量处理及按行操作。 */
export function MonitorWarnPage({
    loadRecords = loadMonitorWarnRecords,
    loadCommissioners = loadMonitorWarnCommissioners,
    assignWarnings = assignMonitorWarnings,
    handleWarnings = handleMonitorWarnings,
    permissions
}: MonitorWarnPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const currentUserName = useUserStore(state => state.userInfo.user?.name || "");
    // 保持 Vue v-auth 语义：显式测试权限优先，否则读取当前登录用户权限。
    const currentPermissions = permissions ?? storePermissions;
    const canAssign = currentPermissions.includes("ROLE_WARNING_MANAGE");
    const canHandle = currentPermissions.includes("ROLE_WARNING_DEAL");
    const [rows, setRows] = useState<MonitorEarlyWarnRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<MonitorWarnQuery>({ page: 1, size: 20 });
    const [keyword, setKeyword] = useState("");
    const [type, setType] = useState<number>();
    const [handleState, setHandleState] = useState<number>(HANDLE_STATE.UNHANDLED.code);
    // 对齐旧 Vue 列表的可选误报筛选，未选择时不向接口传递该参数。
    const [misinformationFilter, setMisinformationFilter] = useState<number>();
    const [timeRange, setTimeRange] = useState<[string | null, string | null] | null>(null);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [officers, setOfficers] = useState<MonitorWarnOfficer[]>([]);
    const [assignOpen, setAssignOpen] = useState(false);
    const [selectedOfficer, setSelectedOfficer] = useState<number>();
    const [handleOpen, setHandleOpen] = useState(false);
    const [handleIds, setHandleIds] = useState<number[]>([]);
    const [remark, setRemark] = useState("");
    const [misinformation, setMisinformation] = useState(0);
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        let disposed = false;
        void loadRecords(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (!disposed) {
                    setRows([]);
                    setTotal(0);
                    setErrorMessage("预警列表加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadRecords, query]);

    const submitFilters = () => {
        const next: MonitorWarnQuery = { page: 1, size: query.size };
        if (keyword.trim()) next.keyword = keyword.trim();
        if (type !== undefined) next.type = type;
        if (handleState !== undefined) next.handleState = handleState;
        if (misinformationFilter !== undefined) next.misinformation = misinformationFilter;
        const beginTime = toDateTime(timeRange?.[0] || null);
        const endTime = toDateTime(timeRange?.[1] || null, true);
        if (beginTime) next.beginTime = beginTime;
        if (endTime) next.endTime = endTime;
        setQuery(next);
    };
    const resetFilters = () => {
        setKeyword("");
        setType(undefined);
        setHandleState(HANDLE_STATE.UNHANDLED.code);
        setMisinformationFilter(undefined);
        setTimeRange(null);
        // 重置时恢复 Vue 搜索项的默认“未处理”状态，而不是清空该业务筛选。
        setQuery({ page: 1, size: 20, handleState: HANDLE_STATE.UNHANDLED.code });
    };
    /** 旧 Vue sort-change 触发后回到第一页并保留当前预警筛选条件。 */
    const handleSortChange = useCallback((sort: { prop?: string; order: "ascending" | "descending" | null }) => {
        setQuery(current => buildMonitorWarnSortQuery(current, sort));
    }, []);
    const openAssign = useCallback(
        async (ids: number[]) => {
            if (!ids.length) {
                message.error("请选择需要批量指派的数据");
                return;
            }
            setSelectedOfficer(undefined);
            setOfficers([]);
            setSelectedIds(ids);
            setAssignOpen(true);
            // 指派弹窗打开后才读取专员，避免列表首屏提前发起无关请求。
            void loadCommissioners()
                .then(setOfficers)
                .catch(() => setOfficers([]));
        },
        [loadCommissioners]
    );
    const submitAssign = async () => {
        if (!selectedOfficer || !selectedIds.length) return;
        setActionLoading(true);
        try {
            await assignWarnings(selectedOfficer, selectedIds);
            message.success("指派成功");
            setAssignOpen(false);
            setSelectedIds([]);
            setQuery(current => ({ ...current }));
        } catch {
            message.error("指派失败");
        } finally {
            setActionLoading(false);
        }
    };
    const openHandle = useCallback((ids: number[]) => {
        if (!ids.length) {
            message.error("请选择需要批量处理的数据");
            return;
        }
        setHandleIds(ids);
        setRemark("");
        setMisinformation(0);
        setHandleOpen(true);
    }, []);
    const submitHandle = async () => {
        if (!handleIds.length) return;
        if (!remark.trim() && misinformation === 0) {
            message.error("请输入备注");
            return;
        }
        setActionLoading(true);
        try {
            // Vue 在误报时允许空备注，并会从批量处理 payload 中移除 remark 字段。
            const form = { ids: handleIds, misinformation } as {
                ids: number[];
                misinformation: number;
                remark?: string;
            };
            if (remark.trim()) form.remark = remark.trim();
            await handleWarnings(form);
            message.success("处理成功");
            setHandleOpen(false);
            setSelectedIds([]);
            setQuery(current => ({ ...current }));
        } catch {
            message.error("处理失败");
        } finally {
            setActionLoading(false);
        }
    };

    // 用 zhihao-ui-react 的列契约承接旧 Vue 预警 ZhTable 的选择列和 operation slot。
    const warningColumns = useMemo<TableColumnProps<MonitorEarlyWarnRecord>[]>(
        () => [
            { type: "selection" },
            {
                key: "id",
                label: "编号",
                prop: "id",
                render: ({ row }) => (
                    <Link aria-label={`预警详情 ${row.id}`} to={`/logistics/monitor/monitorWarn/${row.id}`}>
                        {row.id}
                    </Link>
                )
            },
            {
                key: "type",
                label: "类型",
                prop: "type",
                render: ({ cellValue }) => enumText(EXCEPTION_TYPE, cellValue as number)
            },
            {
                key: "triggerTime",
                label: "触发时间",
                prop: "triggerTime",
                render: ({ cellValue }) =>
                    (cellValue as string | undefined)?.replace("T", " ").slice(0, 19) || DEFAULT_VALUE
            },
            {
                key: "ship",
                label: "船舶",
                prop: "shipCnName",
                render: ({ row }) => row.shipCnName || row.shipName || DEFAULT_VALUE
            },
            {
                key: "handleState",
                label: "处理状态",
                prop: "handleState",
                render: ({ cellValue }) => <Tag>{enumText(HANDLE_STATE, cellValue as number)}</Tag>
            },
            {
                key: "handlerName",
                label: "处理人",
                prop: "handlerName",
                render: ({ cellValue }) => (cellValue as string | undefined) || DEFAULT_VALUE
            },
            {
                key: "misinformation",
                label: "是否误报",
                prop: "misinformation",
                render: ({ cellValue }) => enumText(YES_OR_NO, cellValue as number)
            },
            {
                key: "handleTime",
                label: "处理时间",
                prop: "handleTime",
                sortable: "custom",
                render: ({ cellValue }) =>
                    (cellValue as string | undefined)?.replace("T", " ").slice(0, 19) || DEFAULT_VALUE
            },
            {
                key: "operation",
                label: "操作",
                prop: "operation",
                fixed: "right",
                render: ({ row }) => (
                    <Space>
                        {canAssign && Number(row.handleState) !== HANDLE_STATE.HANDLED.code ? (
                            <Button
                                aria-label={`指派-${row.id}`}
                                disabled={banButton}
                                type="link"
                                onClick={() => void openAssign([row.id])}
                            >
                                指派
                            </Button>
                        ) : null}
                        {canHandle &&
                        Number(row.handleState) !== HANDLE_STATE.HANDLED.code &&
                        row.handlerName === currentUserName ? (
                            <Button
                                aria-label={`处理-${row.id}`}
                                disabled={banButton}
                                type="link"
                                onClick={() => openHandle([row.id])}
                            >
                                处理
                            </Button>
                        ) : null}
                    </Space>
                )
            }
        ],
        [banButton, canAssign, canHandle, currentUserName, openAssign, openHandle]
    );

    return (
        <section aria-label="monitor-warn-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Form layout="inline" onFinish={submitFilters}>
                    <Form.Item label="关键词">
                        <Input
                            aria-label="关键词"
                            placeholder="请输入处理人、船舶"
                            value={keyword}
                            onChange={event => setKeyword(event.target.value)}
                        />
                    </Form.Item>
                    <Form.Item label="类型">
                        <Select
                            aria-label="预警类型"
                            allowClear
                            placeholder="请选择类型"
                            value={type}
                            options={EXCEPTION_TYPE.options.map(item => ({
                                label: item.label,
                                value: Number(item.value)
                            }))}
                            onChange={setType}
                        />
                    </Form.Item>
                    <Form.Item label="触发时间">
                        <ZhDateRangePicker
                            aria-label="触发时间"
                            endPlaceholder="触发结束时间"
                            format="YYYY-MM-DD"
                            startPlaceholder="触发开始时间"
                            type="daterange"
                            value={timeRange}
                            valueFormat="YYYY-MM-DD"
                            onChange={(_value, formattedValue) => setTimeRange(formattedValue)}
                        />
                    </Form.Item>
                    <Form.Item label="处理状态">
                        <Select
                            aria-label="处理状态"
                            value={handleState}
                            options={HANDLE_STATE.options.map(item => ({
                                label: item.label,
                                value: Number(item.value)
                            }))}
                            onChange={setHandleState}
                        />
                    </Form.Item>
                    <Form.Item label="是否误报">
                        <Select
                            aria-label="是否误报"
                            allowClear
                            placeholder="请选择是否误报"
                            value={misinformationFilter}
                            options={[
                                { label: "否", value: 0 },
                                { label: "是", value: 1 }
                            ]}
                            onChange={setMisinformationFilter}
                        />
                    </Form.Item>
                    <Button aria-label="查询" htmlType="submit" type="primary">
                        查询
                    </Button>
                    <Button onClick={resetFilters}>重置</Button>
                </Form>
            </Card>
            <Card>
                <ZhTable<MonitorEarlyWarnRecord>
                    rowKey="id"
                    data={rows}
                    columns={warningColumns}
                    emptyText="暂无数据"
                    onSortChange={handleSortChange}
                    pagination={false}
                    onSelectionChange={selection => setSelectedIds(selection.map(row => Number(row.id)))}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Space className="mb-4">
                                {canAssign ? (
                                    <Button
                                        disabled={banButton}
                                        type="primary"
                                        aria-label="批量指派"
                                        onClick={() => void openAssign(selectedIds)}
                                    >
                                        指派
                                    </Button>
                                ) : null}
                                {canHandle ? (
                                    <Button
                                        disabled={banButton}
                                        type="primary"
                                        aria-label="批量处理"
                                        onClick={() => openHandle(selectedIds)}
                                    >
                                        处理
                                    </Button>
                                ) : null}
                            </Space>

                            {errorMessage ? <Alert role="alert" message={errorMessage} type="error" /> : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                showSizeChanger={false}
                                onChange={page => setQuery(current => ({ ...current, page }))}
                            />
                        </div>
                    }
                />
            </Card>
            <Modal
                title="指派专员"
                open={assignOpen}
                forceRender
                getContainer={false}
                okText="确定指派"
                cancelText="取消"
                confirmLoading={actionLoading}
                onCancel={() => setAssignOpen(false)}
                onOk={() => void submitAssign()}
            >
                {officers.length ? (
                    <Space direction="vertical">
                        {officers.map(item => (
                            <Checkbox
                                key={item.user.id}
                                checked={selectedOfficer === item.user.id}
                                onChange={() => setSelectedOfficer(item.user.id)}
                            >
                                {item.user.name}
                                {item.user.employeeId ? `（${item.user.employeeId}）` : ""}
                            </Checkbox>
                        ))}
                    </Space>
                ) : (
                    "暂未找到可指派的服务人员"
                )}
            </Modal>
            <Modal
                title="处理"
                open={handleOpen}
                okText="提交处理"
                cancelText="取消"
                confirmLoading={actionLoading}
                onCancel={() => setHandleOpen(false)}
                onOk={() => void submitHandle()}
            >
                <Form layout="vertical">
                    <Form.Item label="是否误报">
                        <Select
                            aria-label="是否误报"
                            value={misinformation}
                            options={[
                                { value: 0, label: "否" },
                                { value: 1, label: "是" }
                            ]}
                            onChange={setMisinformation}
                        />
                    </Form.Item>
                    <Form.Item label="备注">
                        <Input.TextArea
                            aria-label="备注"
                            maxLength={200}
                            value={remark}
                            onChange={event => setRemark(event.target.value)}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </section>
    );
}
